import { Box, Chip, Typography, useTheme, type Theme } from '@mui/material';
import { useMemo, type ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import PropertiesPanel from '~/components/Properties/PropertiesPanel';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import StatusLine from '~/components/Status/StatusLine';
import { cutUID } from '~/utils/string';
import { formatMmAsCmAtScale } from '~/utils/units';
import { datasetInfo, type ApprovalTone, type DatasetInfoRow } from '../model';
import ModalityChip from '~/components/Chip/ModalityChip';

const APPROVAL_COLORS: Record<ApprovalTone, (theme: Theme) => string> = {
    approved: (theme) => theme.palette.success.main,
    unapproved: (theme) => theme.palette.secondary.main,
    rejected: (theme) => theme.palette.error.main,
};

type DatasetInfoTooltipProps = {
    /** Whether the details show; driven by the control's hover, focus and pin state. */
    open: boolean;
    /** Called when the details are dismissed from within (Escape). */
    onClose: () => void;
    series: SeriesDto;
    /** Every series of the same patient, to name what this one references. */
    siblings: readonly SeriesDto[];
    /** The details control the card describes. */
    children: ReactElement;
};

/**
 * The details of one dataset as the properties tooltip of its ⓘ control:
 * the facts the connector recorded, its approval state, and the first few
 * structures of a structure set. Read-only and free of anything focusable,
 * so it can be the control's description.
 */
export default function DatasetInfoTooltip({
    open,
    onClose,
    series,
    siblings,
    children,
}: DatasetInfoTooltipProps) {
    const intl = useIntl();
    const theme = useTheme();
    // Built once per dataset, not per showing: a card that is closing keeps
    // its content through the fade, so it cannot be gated on `open`.
    const info = useMemo(
        () => datasetInfo(intl, series, siblings),
        [intl, series, siblings]
    );
    return (
        <PropertiesTooltip
            open={open}
            onClose={onClose}
            placement="bottom-end"
            describeChild
            disableInteractive
            disableHoverListener
            disableFocusListener
            disableTouchListener
            title={
                // Sized by the theme's tooltip width; the name yields to the status.
                <Box sx={{ minWidth: 260 }}>
                    <PropertiesPanel showTitle={false}>
                        <Box
                            sx={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                            }}
                        >
                            <ModalityChip modality={info.modality} />
                            <Typography
                                fontSize={12}
                                fontWeight={500}
                                noWrap
                                sx={{ flex: 1, minWidth: 0 }}
                            >
                                <SensitiveInfoTypography>
                                    {info.name}
                                </SensitiveInfoTypography>
                            </Typography>
                            {info.approval && (
                                <StatusLine
                                    color={APPROVAL_COLORS[info.approval.tone](
                                        theme
                                    )}
                                    label={
                                        info.approval.reviewer
                                            ? `${info.approval.label} · ${info.approval.reviewer}`
                                            : info.approval.label
                                    }
                                    size={11}
                                />
                            )}
                        </Box>
                        {info.rows.map((row) => (
                            <InfoRow key={row.labelId} row={row} />
                        ))}
                        {info.structures && (
                            <>
                                <LabelText>
                                    <FormattedMessage id="patientList.dataset.info.structures" />
                                </LabelText>
                                <ValueText component="span">
                                    <FormattedMessage
                                        id="patientList.dataset.roiCount"
                                        values={{
                                            count: info.structures.total,
                                        }}
                                    />
                                </ValueText>
                                <Box
                                    sx={{
                                        gridColumn: '1 / -1',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                    }}
                                >
                                    {/* Keyed by position: a set can hold
                                        two ROIs of one name, and the list
                                        is rebuilt in ROI order. */}
                                    {info.structures.shown.map(
                                        (structure, index) => (
                                            <Chip
                                                key={index}
                                                variant="tag"
                                                label={structure.name}
                                                icon={
                                                    structure.color ? (
                                                        <Box
                                                            component="span"
                                                            aria-hidden
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: 0.5,
                                                                bgcolor:
                                                                    structure.color,
                                                            }}
                                                        />
                                                    ) : undefined
                                                }
                                            />
                                        )
                                    )}
                                    {info.structures.hidden > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            <FormattedMessage
                                                id="patientList.dataset.info.moreStructures"
                                                values={{
                                                    count: info.structures
                                                        .hidden,
                                                }}
                                            />
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ gridColumn: '1 / -1', mt: 0.5 }}
                        >
                            <FormattedMessage id="patientList.dataset.info.pinHint" />
                        </Typography>
                    </PropertiesPanel>
                </Box>
            }
        >
            {children}
        </PropertiesTooltip>
    );
}

/** One label/value pair of the details grid, rendered by its kind. */
function InfoRow({ row }: { row: DatasetInfoRow }) {
    return (
        <>
            <LabelText>
                <FormattedMessage id={row.labelId} />
            </LabelText>
            <ValueText component="span">
                <InfoValue row={row} />
            </ValueText>
        </>
    );
}

function InfoValue({ row }: { row: DatasetInfoRow }) {
    switch (row.kind) {
        case 'sensitive':
            return (
                <SensitiveInfoTypography>{row.value}</SensitiveInfoTypography>
            );
        case 'uid':
            return (
                <SensitiveInfoTypography>
                    {cutUID(row.value) ?? row.value}
                </SensitiveInfoTypography>
            );
        case 'lengthMm':
            // Domain mm to UI cm here, at the boundary; the unit travels with the value.
            return <>{formatMmAsCmAtScale(row.valueMm, row.scale)} cm</>;
        case 'text':
            return <>{row.value}</>;
    }
}
